import { useState } from 'react';

import { IconArrowDown, IconArrowUp } from '@iwoca/icons';
import { GetSellerPayLinkResponse } from '@iwoca/lapi-client/iwocapay';
import { Button, Table } from '@iwoca/orion';

import { buildQueryString } from '../../../../Buyer/utils/queryParams';
import { displayCurrency } from '../../../../utils/DisplayCurrency';
import { productDurationToReadable } from '../../../../utils/ProductDuration';
import { formatDate } from '../../../Fees/RateContextHeader/RateContextHeader';
import { usePayLinks } from '../../../hooks/usePaylinks.hook';

const VISIBLE_PAYMENTS = 4;

type PayLink = GetSellerPayLinkResponse['data'][number];

// Undated links sort last in both directions, so a missing date never takes the
// top slot away from a real payment.
const sortByDate = (payLinks: PayLink[], direction: 'asc' | 'desc') => {
  const dated = payLinks.filter((payLink) => payLink.created);
  const undated = payLinks.filter((payLink) => !payLink.created);

  const sorted = [...dated].sort((a, b) => {
    const difference =
      new Date(a.created!).getTime() - new Date(b.created!).getTime();
    return direction === 'asc' ? difference : -difference;
  });

  return [...sorted, ...undated];
};

export const LatestPaymentsTable = ({
  buyerStateKey,
  buyerName,
}: {
  buyerStateKey: string;
  buyerName: string;
}) => {
  // `PAID` is the funded set: the status annotation resolves it from
  // `funded_at`, and filtering it server-side keeps funded links from being
  // paginated away before the client can see them.
  const { payLinks, isLoading } = usePayLinks({
    buyerStateKey,
    status: 'PAID',
    includeArchivedAndUnarchived: true,
  });
  const [direction, setDirection] = useState<'asc' | 'desc'>('desc');

  const visiblePayLinks = sortByDate(payLinks, direction).slice(
    0,
    VISIBLE_PAYMENTS,
  );
  // Date is the only column and is always sorted, so there is no neutral
  // IconSort state here as there is on the customers table.
  const DirectionIcon = direction === 'asc' ? IconArrowUp : IconArrowDown;
  const queryString = buildQueryString({ search: buyerName });

  return (
    <div>
      <div className="mb-l text-l font-med text-primary-10">
        Latest payments
      </div>

      <Table.Root className="w-full [&_td]:px-l [&_td]:py-r">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell
              className="w-1/4 text-xs font-med uppercase text-structure-40"
              aria-sort={direction === 'asc' ? 'ascending' : 'descending'}
            >
              <button
                type="button"
                className="flex cursor-pointer items-center gap-xs border-none bg-transparent p-0 font-[inherit] text-[inherit]"
                onClick={() =>
                  setDirection((current) =>
                    current === 'desc' ? 'asc' : 'desc',
                  )
                }
              >
                Date
                <DirectionIcon height={14} width={14} aria-hidden />
              </button>
            </Table.HeaderCell>
            <Table.HeaderCell className="w-1/2 text-xs font-med uppercase text-structure-40">
              Invoice &amp; client
            </Table.HeaderCell>
            <Table.HeaderCell className="w-1/4 text-xs font-med uppercase text-structure-40">
              Amount
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {visiblePayLinks.map((payLink) => (
            <Table.Row key={payLink.id}>
              <Table.Cell className="w-1/4 !items-center !text-s font-med text-primary-10">
                {payLink.created ? formatDate(payLink.created, 'short') : '—'}
              </Table.Cell>
              <Table.Cell className="w-1/2">
                <div className="flex flex-col gap-xs">
                  <div className="text-s font-med text-primary-10">
                    {payLink.reference}
                  </div>
                  <div className="text-xs font-reg text-primary-10">
                    {payLink.buyer_name}
                  </div>
                </div>
              </Table.Cell>
              <Table.Cell className="w-1/4">
                <div className="flex flex-col gap-xs">
                  <div className="text-s font-med text-primary-10">
                    {displayCurrency(payLink.amount)}
                  </div>
                  <div className="text-xs font-reg text-structure-40">
                    {productDurationToReadable(payLink.product_duration)}
                  </div>
                </div>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>

      {!isLoading && visiblePayLinks.length === 0 && (
        <div className="py-xl text-center text-m font-reg text-structure-40">
          No payments yet
        </div>
      )}

      <div className="mt-l flex justify-center">
        <Button
          href={`/pay/payments/${queryString}`}
          className="!px-2xl"
          variant="secondary"
        >
          See older payments
        </Button>
      </div>
    </div>
  );
};
